import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useUsers, isEmployeeUser } from "@/hooks/use-users";
import { useChatActions } from "@/hooks/use-chat-actions";
import { cn } from "@/lib/utils";
import { PresenceDot } from "@/components/shared/PresenceDot";
import { SrIcon } from "@/components/ui/SrIcon";

type Mode = "direct" | "group";

export function NewChatDialog({
  open,
  onOpenChange,
  currentUserId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentUserId: string;
  onCreated: (roomId: string) => void;
}) {
  const { data: users, isLoading } = useUsers();
  const { createDirectChat, createGroupChat } = useChatActions(currentUserId);
  const [mode, setMode] = useState<Mode>("direct");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [groupName, setGroupName] = useState("");
  const isBusy = createDirectChat.isPending || createGroupChat.isPending;

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return (users ?? [])
      // VIB-1055: external-brukere skal ikke kunne velges til DM eller gruppe.
      .filter(isEmployeeUser)
      .filter((u) => u.id !== currentUserId)
      .filter((u) => !q || u.full_name.toLowerCase().includes(q));
  }, [users, search, currentUserId]);

  const reset = () => {
    setSearch("");
    setSelected(new Set());
    setGroupName("");
    setMode("direct");
  };

  const handleSubmit = async () => {
    try {
      if (mode === "direct") {
        const userId = Array.from(selected)[0];
        if (!userId) return;
        const roomId = await createDirectChat.mutateAsync(userId);
        reset();
        onOpenChange(false);
        onCreated(roomId);
      } else {
        if (selected.size === 0 || !groupName.trim()) return;
        const roomId = await createGroupChat.mutateAsync({
          name: groupName,
          memberIds: Array.from(selected),
        });
        reset();
        onOpenChange(false);
        onCreated(roomId);
      }
    } catch {
      // Mutation-hooken viser den konkrete feilen. Behold dialogen åpen og
      // konsumer rejection-en så den ikke rapporteres som unhandled i Sentry.
    }
  };

  const canSubmit =
    !isBusy &&
    ((mode === "direct" && selected.size === 1) ||
      (mode === "group" && selected.size >= 1 && groupName.trim().length > 0));

  const toggleUser = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (mode === "direct") {
        next.clear();
        next.add(id);
        return next;
      }
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Ny chat</DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 p-0.5 rounded-md bg-muted w-fit">
          <button
            type="button"
            onClick={() => { setMode("direct"); setSelected(new Set()); }}
            className={cn(
              "px-3 py-1 text-xs rounded",
              mode === "direct" ? "bg-background shadow-sm" : "text-muted-foreground",
            )}
          >
            Direktemelding
          </button>
          <button
            type="button"
            onClick={() => { setMode("group"); setSelected(new Set()); }}
            className={cn(
              "px-3 py-1 text-xs rounded inline-flex items-center gap-1",
              mode === "group" ? "bg-background shadow-sm" : "text-muted-foreground",
            )}
          >
            <SrIcon name="users" className="w-3 h-3"  /> Gruppe
          </button>
        </div>

        {mode === "group" && (
          <input
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Gruppenavn"
            className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
          />
        )}

        <div className="relative">
          <SrIcon name="search" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground"  />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søk etter person..."
            className="w-full h-9 rounded-md border bg-background pl-8 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="max-h-64 overflow-y-auto rounded border">
          {isLoading ? (
            <div className="flex justify-center py-4">
              <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground"  />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-xs text-muted-foreground py-4">Ingen treff</p>
          ) : (
            filtered.map((u) => {
              const isSelected = selected.has(u.id);
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => toggleUser(u.id)}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-muted/50 border-b last:border-b-0 border-border/40",
                    isSelected && "bg-primary/10",
                  )}
                >
                  <div className="relative shrink-0">
                    {u.avatar_url ? (
                      <img src={u.avatar_url} alt={u.full_name} className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <span className="w-7 h-7 rounded-full bg-primary/10 text-primary text-[10px] font-medium inline-flex items-center justify-center">
                        {u.full_name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                      </span>
                    )}
                    <PresenceDot userId={u.id} size="sm" className="absolute -bottom-0.5 -right-0.5" />
                  </div>
                  <span className="flex-1 text-sm truncate">{u.full_name}</span>
                  {isSelected && <SrIcon name="check" className="w-4 h-4 text-primary shrink-0"  />}
                </button>
              );
            })
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }}>
            Avbryt
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isBusy ? <SrIcon name="loader2" className="w-4 h-4 mr-1.5 animate-spin"  /> : null}
            {mode === "direct" ? "Start samtale" : "Opprett gruppe"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default NewChatDialog;
